import { GlobalToken } from 'antd';
import styled from 'styled-components';
import { breakpoints } from '@/utils/util-breakpoints';
import { ExclamationCircleOutlined } from '@ant-design/icons';

export const ErrorIcon = styled(ExclamationCircleOutlined)<{ theme?: { token: GlobalToken } }>`
  color: ${({ theme }) => theme?.token?.colorError};
  cursor: help;
  position: absolute;
  right: -12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
`;

export const RoomConfigurationStepFormStyles = styled.div<{ theme: { token: GlobalToken } }>`
  background-color: transparent;
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  text-align: left;

  .arrow-icon {
    transition: transform 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${({ theme }) => theme?.token?.fontSizeIcon || '0.75rem'};

    &.open {
      transform: rotate(180deg);
    }

    &.closed {
      transform: rotate(0);
    }
  }

  .form-header {
    color: ${({ theme: { token } }) => token.colorPrimaryText};
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.75rem;
    margin-bottom: 1.5rem;
    width: 100%;
    text-align: left;
  }

  .form-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .customer-info-section {
    margin-bottom: 1.5rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .customer-name {
    color: ${({ theme: { token } }) => token.colorText};
    font-size: 1.5rem;
    font-weight: 600;
    line-height: 2rem;
    margin: 0;
  }

  .account-details-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .account-id-wrapper {
    display: flex;
    flex: 1;
  }

  .account-id-label {
    color: ${({ theme: { token } }) => token.colorTextSecondary};
    font-size: 0.875rem;
    line-height: 1.375rem;
    margin-right: 0.5rem;
  }

  .account-id-value {
    color: ${({ theme: { token } }) => token.colorText};
    font-size: 1rem;
    line-height: 1.5rem;
  }

  .tank-form-item {
    margin-bottom: 0;
    margin-left: auto;
    justify-content: flex-end;
  }

  .ant-form-item-explain-error {
    position: absolute;
  }

  .compressor-select {
    .ant-select-selector {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  .compressor-select,
  .spec-input-number,
  .compressor-number-input,
  .serial-number-input,
  .model-input {
    margin-bottom: 0;
  }

  .tier-tag {
    margin-left: 0.5rem;
    color: ${({ theme: { token } }) => token.colorPrimary};
    font-size: 0.875rem;
  }

  .info-cards-row {
    display: flex;
    flex-direction: row;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
    width: 100%;
  }

  .info-card-wrapper {
    flex: 1;
    min-width: 0;
  }

  .info-card {
    width: 100%;
    height: 100%;
    border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  }

  .info-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border: 0.0625rem solid ${({ theme: { token } }) => token.colorBorderSecondary};
    border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px
      ${({ theme: { token } }) => token.borderRadiusLG}px 0 0;
  }

  .info-card-title-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .info-card-title {
    color: ${({ theme: { token } }) => token.colorText};
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.5rem;
  }

  .edit-button {
    color: ${({ theme: { token } }) => token.colorPrimary};
    font-size: 0.875rem;
    cursor: pointer;
  }

  .icon-avatar {
    background-color: ${({ theme: { token } }) => token.colorPrimaryBg};

    .anticon {
      color: ${({ theme: { token } }) => token.colorPrimary};
    }
  }

  .info-card-content {
    padding: 1rem 3rem;
    border-right: 0.0625rem solid ${({ theme: { token } }) => token.colorBorderSecondary};
    border-bottom: 0.0625rem solid ${({ theme: { token } }) => token.colorBorderSecondary};
    border-left: 0.0625rem solid ${({ theme: { token } }) => token.colorBorderSecondary};
    border-radius: 0 0 ${({ theme: { token } }) => token.borderRadiusLG}px
      ${({ theme: { token } }) => token.borderRadiusLG}px;
    height: 5.5rem;
    overflow: hidden;
  }

  .truncated-text {
    color: ${({ theme: { token } }) => token.colorText};
    font-size: 0.875rem;
    line-height: 1.375rem;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .address-details-row {
    display: flex;
    gap: 0.5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .association-section {
    display: flex;
    align-items: center;
    gap: 1rem;
    width: 100%;
    justify-content: flex-start;
    align-self: flex-start;
  }

  .association-text-container {
    flex: 1;
  }

  .association-title {
    color: ${({ theme: { token } }) => token.colorText};
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.5rem;
    display: block;
  }

  .association-description {
    color: ${({ theme: { token } }) => token.colorTextSecondary};
    font-size: 0.875rem;
    line-height: 1.375rem;
  }

  .account-select {
    width: 18.375rem;
  }

  .account-form-item {
    margin-bottom: 0;
    flex-shrink: 0;
  }

  .form-actions {
    display: flex;
    justify-content: flex-start;
    margin-top: 2rem;
  }

  @media (max-width: 767px) {
    .info-cards-row {
      flex-direction: column;
      gap: 1rem;
    }

    .info-card-wrapper {
      width: 100%;
      margin-bottom: 1rem;
    }

    .account-details-row {
      flex-direction: column;
      align-items: flex-start;
    }

    .association-section {
      flex-direction: column;
      align-items: flex-start;
    }

    .account-select {
      width: 100%;
    }

    .account-form-item {
      width: 100%;
      margin-top: 1rem;
    }

    .tank-form-item {
      width: 100%;
      margin-top: 0.5rem;
    }
  }

  .step-container {
    width: 100%;
    max-width: 62.625rem;
    margin: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    color: ${({ theme }) => theme.token.colorText};

    @media (max-width: ${breakpoints.lg}px) {
      max-width: 100%;
    }
  }

  .expanded-customer-information-step-form,
  .expanded-site-configuration-step-form {
    width: 100%;
  }

  .collapsible-section {
    width: 100%;
    max-width: 62.625rem;
    margin-bottom: ${({ theme }) => theme.token.marginLG}px;
    margin-left: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    background-color: ${({ theme }) => theme.token.colorBgContainer};
    border-radius: ${({ theme }) => theme.token.borderRadiusSM}px;
    padding: 0;
    position: relative;

    @media (max-width: ${breakpoints.lg}px) {
      max-width: 100%;
    }

    .collapsed-customer-information-step-form,
    .collapsed-site-configuration-step-form {
      width: 100%;
      max-width: 62.625rem;
      padding: 1.5rem;
      color: ${({ theme }) => theme.token.colorText};
      position: relative;
      margin: 0;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      overflow: visible;

      @media (max-width: ${breakpoints.lg}px) {
        max-width: 100%;
      }

      @media (max-width: ${breakpoints.md}px) {
        padding: 16px 16px;
      }

      .form-header {
        color: ${({ theme }) => theme.token.colorPrimaryText};
        font-size: 20px;
        font-weight: 600;
        line-height: 28px;
        padding-right: 40px;
        display: inline-block;
        width: calc(100% - 40px);
        text-align: left;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        box-sizing: border-box;
        position: relative;
        z-index: 1;
        word-wrap: break-word;
      }

      .customer-name {
        color: ${({ theme }) => theme.token.colorText};
        font-size: ${({ theme }) => theme.token.fontSizeXL}px;
        font-weight: 600;
        line-height: ${({ theme }) => theme.token.lineHeightHeading4 || 2}rem;
        margin: 0 0 ${({ theme }) => theme.token.marginXS}px;
      }

      .account-details-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: ${({ theme }) => theme.token.margin}px;

        @media (max-width: ${breakpoints.md}px) {
          flex-direction: column;
          align-items: flex-start;
        }
      }

      .account-id-wrapper {
        display: flex;
        align-items: center;
        margin-bottom: 0;
        flex-wrap: wrap;
        gap: ${({ theme }) => theme.token.marginXS}px;

        .tier-tag {
          margin-left: ${({ theme }) => theme.token.marginXS}px;
          margin-right: 0;
          font-size: ${({ theme }) => theme.token.fontSizeSM}px;
          padding: ${({ theme }) => theme.token.paddingXXS}px ${({ theme }) => theme.token.paddingXS}px;
          border-radius: ${({ theme }) => theme.token.borderRadiusSM}px;
          background-color: ${({ theme }) => theme.token.colorPrimaryBg};
          color: ${({ theme }) => theme.token.colorPrimary};
          border: none;
        }
      }

      .account-id-label {
        color: ${({ theme }) => theme.token.colorTextSecondary};
        font-size: ${({ theme }) => theme.token.fontSizeSM}px;
        line-height: ${({ theme }) => theme.token.lineHeight}px;
        margin-right: ${({ theme }) => theme.token.marginXS}px;
      }

      .account-id-value {
        color: ${({ theme }) => theme.token.colorText};
        font-size: ${({ theme }) => theme.token.fontSize}px;
        line-height: ${({ theme }) => theme.token.lineHeight}px;
      }
    }

    .embedded-customer-information-step-form,
    .embedded-site-configuration-step-form {
      position: relative;
      width: 100%;
      max-width: 62.625rem;
      margin: 0;
      
      @media (max-width: ${breakpoints.lg}px) {
        max-width: 100%;
      }
        
      > div {
        padding: 0;
      }
    }
  }

  .step-toggle-button {
    position: absolute;
    top: 16px;
    right: 24px;
    left: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    cursor: pointer;
    color: ${({ theme }) => theme?.token?.colorText};
    background-color: transparent;
    border-radius: 0;
    z-index: 10;
    transform: translateX(0);

    &:hover {
      opacity: 0.8;
    }

    &.expanded-toggle {
      top: ${({ theme }) => theme?.paddingLG || '1.5rem'};
      right: ${({ theme }) => theme?.paddingLG || '1.5rem'};
    }
  }

  .collapsible-content {
    width: 100%;
    overflow: hidden;
    position: relative;

    &.open {
      max-height: 3000px;
      transition: max-height 0.6s ease-in-out;

      .embedded-customer-information-step-form,
      .embedded-site-configuration-step-form {
        padding: 0;
        opacity: 1;
        transition: opacity 0.4s ease-in-out;
        transition-delay: 0.1s;
      }
    }

    &.closed {
      max-height: 0;
      transition: max-height 0.6s ease-in-out;

      .embedded-customer-information-step-form,
      .embedded-site-configuration-step-form {
        padding: 0;
        opacity: 0;
        transition: opacity 0.4s ease-in-out;
      }
    }

    .embedded-customer-information-step-form,
    .embedded-site-configuration-step-form {
      position: relative;
      max-width: 100%;
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
    }
  }
`;
